import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BadgeCheck } from "lucide-react";

/** Live ticker of the last N HLL verifications. Polls every `intervalMs` ms.
 *  Flashes cyan when a new verification lands. */
export default function VerifyTicker({ limit = 5, intervalMs = 15000 }) {
  const [rows, setRows] = useState([]);
  const [flashKey, setFlashKey] = useState(null);
  const topKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/verifications/recent?limit=${limit}`);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const nextTop = list[0] ? `${list[0].player_id}:${list[0].verified_at}` : null;
        if (topKeyRef.current && nextTop && nextTop !== topKeyRef.current) {
          setFlashKey(nextTop);
          setTimeout(() => setFlashKey((k) => (k === nextTop ? null : k)), 2400);
        }
        topKeyRef.current = nextTop;
        setRows(list);
      } catch { /* silent — ticker is best-effort */ }
    };
    load();
    const t = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [limit, intervalMs]);

  const relTime = (iso) => {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  if (rows.length === 0) {
    return (
      <div
        data-testid="verify-ticker-empty"
        className="border border-[#232330] bg-[#0a0a12] px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-2"
      >
        <BadgeCheck className="w-3.5 h-3.5 text-cyan-400" />
        Verify feed idle — no HLL verifications yet.
      </div>
    );
  }

  return (
    <div
      data-testid="verify-ticker"
      className="border border-cyan-400/40 bg-[#0a0a12] overflow-hidden"
    >
      <div className="px-4 py-2 border-b border-[#232330] flex items-center gap-2 bg-[#101018]">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
          Verify feed · live
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 ml-auto">
          last {rows.length}
        </div>
      </div>
      <ul className="divide-y divide-[#232330]">
        {rows.map((r) => {
          const key = `${r.player_id}:${r.verified_at}`;
          const isFlash = flashKey === key;
          return (
            <li
              key={key}
              data-testid={`verify-ticker-row-${r.handle}`}
              className={`px-4 py-2 flex items-center gap-3 transition-colors ${
                isFlash ? "bg-cyan-400/15" : "bg-transparent"
              }`}
            >
              <BadgeCheck className={`w-3.5 h-3.5 shrink-0 ${isFlash ? "text-cyan-300" : "text-cyan-400"}`} />
              <div className="min-w-0 flex-1 flex items-baseline gap-2">
                <span className="font-heading font-bold uppercase tracking-tight text-white truncate">
                  {r.handle}
                </span>
                <span
                  className="text-[10px] font-mono uppercase tracking-widest shrink-0"
                  style={{ color: r.team_color || "#F4D03F" }}
                >
                  {r.team_tag}
                </span>
              </div>
              <div className="text-[10px] font-mono tracking-widest text-gray-500 shrink-0 hidden sm:block">
                by {r.verified_by || "system"}
              </div>
              <div className="text-[10px] font-mono tracking-widest text-cyan-400 shrink-0">
                {relTime(r.verified_at)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
